import React, { useCallback, useMemo, useRef, useState } from 'react'
import { cn } from 'ethcss'
import { color } from 'theme'
import { createElement, elementTypeFromFile } from '../elements/createElement'
import { clientToLogical, clampLogicalPoint } from '../elements/coords'
import { placeBoxAtPoint } from '../elements/placementMetrics'
import { useTemplateEditorContext } from '../hooks/templateEditorContext'
import { CatalogFileItem } from '../templateEditor.types'
import { toDisplaySize } from '../templateEditor.utils'
import { buildElementContextMenuGroups } from './templateEditor__buildElementContextMenu'
import { TemplateEditorCanvasElement } from './templateEditor__canvasElement'
import { TemplateEditorContextMenu, ContextMenuAction } from './templateEditor__contextMenu'
import { TemplateEditorGuideLines } from './templateEditor__guideLines'
import { TemplateEditorMarquee } from './templateEditor__marquee'
import { TemplateEditorPlacePreview } from './templateEditor__placePreview'
import styles from './templateEditor__canvas.jcss'
import { ResizeHandle } from '../templateEditor.types'

export const TemplateEditorCanvas = () => {
    const {
        viewportRef,
        frameRef,
        theme,
        canvasW,
        canvasH,
        canvasBg,
        canvasBgMedia,
        zoom,
        showGrid,
        gridSize,
        elements,
        selectedIds,
        clearSelection,
        startDragMove,
        startResize,
        startMarqueeSelect,
        inlineEdit,
        isPlacing,
        placeMode,
        previewRect,
        previewOpts,
        startPlaceDrag,
        exitPlaceMode,
        guides,
        marqueeRect,
        replaceImageContent,
        catalog,
        insertElement,
        insertTableRowAt,
        insertTableColAt,
        removeTableRowAt,
        removeTableColAt,
        resizeTableRow,
        resizeTableCol,
        insertListItemAt,
        removeListItemAt,
        pushHistorySnapshot,
        bringToFront,
        sendToBack,
        bringForward,
        sendBackward,
        selectElement,
        duplicateSelection,
        deleteSelection,
        startImageReplaceMode,
    } = useTemplateEditorContext()

    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; groups: ContextMenuAction[][] } | null>(null)

    const displayW = toDisplaySize(canvasW, theme.displayScale)
    const displayH = toDisplaySize(canvasH, theme.displayScale)
    const gridStepDisplay = toDisplaySize(gridSize, theme.displayScale)
    const gridColor = `${theme.demoFill}47`
    const pointerScale = theme.displayScale * zoom

    const elementActions = useMemo(
        () => ({
            replaceImage: replaceImageContent,
            catalogPayloadFromItem: catalog.catalogPayloadFromItem,
            insertTableRow: insertTableRowAt,
            insertTableCol: insertTableColAt,
            resizeTableRow,
            resizeTableCol,
            onTableResizeGestureStart: pushHistorySnapshot,
            insertListItem: insertListItemAt,
        }),
        [
            catalog.catalogPayloadFromItem,
            insertListItemAt,
            insertTableColAt,
            insertTableRowAt,
            pushHistorySnapshot,
            replaceImageContent,
            resizeTableCol,
            resizeTableRow,
        ]
    )

    const startDragMoveRef = useRef(startDragMove)
    const startResizeRef = useRef(startResize)
    const isPlacingRef = useRef(isPlacing)
    startDragMoveRef.current = startDragMove
    startResizeRef.current = startResize
    isPlacingRef.current = isPlacing

    const handleElementSelectMove = useCallback((elementId: string, event: React.MouseEvent) => {
        if (isPlacingRef.current) {
            return
        }
        startDragMoveRef.current(elementId, event)
    }, [])

    const handleElementResize = useCallback((elementId: string, handle: ResizeHandle, event: React.MouseEvent) => {
        startResizeRef.current(elementId, handle, event)
    }, [])

    const visibleElements = useMemo(() => {
        const hiddenGroupIds = new Set(
            elements.filter((element) => element.type === 'group' && element.hidden).map((element) => element.id)
        )

        return elements.filter((element) => {
            if (element.hidden) {
                return false
            }
            if (element.groupId && hiddenGroupIds.has(element.groupId)) {
                return false
            }
            return true
        })
    }, [elements])

    const handleFrameMouseDown = (event: React.MouseEvent) => {
        if (isPlacing) {
            startPlaceDrag(event)
            return
        }

        if (event.target !== event.currentTarget) {
            return
        }

        startMarqueeSelect(event, event.currentTarget as HTMLElement)
    }

    const handleCatalogDrop = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault()
            event.stopPropagation()
            if (isPlacing) {
                return
            }

            const raw = event.dataTransfer.getData('application/json')
            if (!raw) {
                return
            }

            let item: CatalogFileItem
            try {
                item = JSON.parse(raw) as CatalogFileItem
            } catch {
                return
            }

            const placeType = elementTypeFromFile(item.fileType)
            const payload = catalog.catalogPayloadFromItem(item)
            if (!placeType || !payload || !frameRef.current) {
                return
            }

            const frameRect = frameRef.current.getBoundingClientRect()
            const logical = clientToLogical(event.clientX, event.clientY, frameRect, theme.displayScale, zoom)
            const point = clampLogicalPoint(logical.x, logical.y, canvasW, canvasH)
            const element = createElement({
                type: placeType,
                center: point,
                opts: payload,
                theme,
            })
            const box = placeBoxAtPoint(point.x, point.y, element.w, element.h, canvasW, canvasH, theme.minElSize)
            insertElement({ ...element, ...box })
            selectElement(element.id)
        },
        [canvasH, canvasW, catalog, frameRef, insertElement, isPlacing, selectElement, theme, zoom]
    )

    const openElementContextMenu = (elementId: string, event: React.MouseEvent) => {
        if (isPlacing) {
            return
        }

        event.preventDefault()
        event.stopPropagation()

        if (!selectedIds.includes(elementId)) {
            selectElement(elementId, false, true)
        }

        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            groups: buildElementContextMenuGroups({
                elementId,
                element: elements.find((item) => item.id === elementId),
                clientX: event.clientX,
                clientY: event.clientY,
                frameRect: frameRef.current?.getBoundingClientRect() ?? null,
                displayScale: theme.displayScale,
                zoom,
                theme,
                zOrder: { bringToFront, bringForward, sendBackward, sendToBack },
                structure: {
                    insertTableRowAt,
                    insertTableColAt,
                    removeTableRowAt,
                    removeTableColAt,
                    insertListItemAt,
                    removeListItemAt,
                },
                edit: { duplicateSelection, deleteSelection, startImageReplaceMode },
            }),
        })
    }

    const openElementContextMenuRef = useRef(openElementContextMenu)
    openElementContextMenuRef.current = openElementContextMenu

    const handleElementContextMenu = useCallback((elementId: string, event: React.MouseEvent) => {
        openElementContextMenuRef.current(elementId, event)
    }, [])

    return (
        <main className={cn(styles.canvasArea, isPlacing && styles.canvasAreaPlacing, isPlacing && 'canvas--placing')}>
            <div
                className={styles.rulerH}
                aria-hidden="true"
                style={{
                    backgroundImage: `repeating-linear-gradient(90deg, ${color.borderDarkTertiary} 0 1px, transparent 1px ${gridStepDisplay}px)`,
                }}
            />
            <div
                className={styles.rulerV}
                aria-hidden="true"
                style={{
                    backgroundImage: `repeating-linear-gradient(${color.borderDarkTertiary} 0 1px, transparent 1px ${gridStepDisplay}px)`,
                }}
            />
            <div
                className={styles.viewport}
                id="viewport"
                ref={viewportRef}
                onMouseDown={() => {
                    if (!isPlacing) {
                        clearSelection()
                    }
                }}
                onContextMenu={(event) => {
                    if (isPlacing) {
                        event.preventDefault()
                        exitPlaceMode()
                    }
                }}
            >
                <div
                    className={styles.zoomHost}
                    data-template-editor-zoom-host=""
                    style={zoom === 1 ? undefined : ({ zoom } as React.CSSProperties)}
                >
                    <div className={styles.wrapper}>
                        <div
                            ref={frameRef}
                            className={styles.frame}
                            data-logical-w={canvasW}
                            data-logical-h={canvasH}
                            style={{
                                width: displayW,
                                height: displayH,
                                background: canvasBgMedia ? 'transparent' : canvasBg,
                                cursor: isPlacing ? 'crosshair' : undefined,
                            }}
                            onMouseDown={(event) => {
                                event.stopPropagation()
                                handleFrameMouseDown(event)
                            }}
                            onDragOver={(event) => {
                                event.preventDefault()
                                event.dataTransfer.dropEffect = 'copy'
                            }}
                            onDrop={handleCatalogDrop}
                        >
                            {canvasBgMedia?.fileType === 'image' && canvasBgMedia.src && (
                                <img
                                    className={styles.bgMedia}
                                    src={canvasBgMedia.thumbnail || canvasBgMedia.src}
                                    alt=""
                                    draggable={false}
                                />
                            )}
                            {canvasBgMedia?.fileType === 'video' && canvasBgMedia.src && (
                                <video
                                    className={styles.bgMedia}
                                    src={canvasBgMedia.src}
                                    poster={canvasBgMedia.thumbnail}
                                    muted
                                    loop
                                    autoPlay
                                />
                            )}
                            {showGrid && (
                                <div
                                    className={styles.grid}
                                    style={{
                                        backgroundImage: `
                                            linear-gradient(to right, ${gridColor} 1px, transparent 1px),
                                            linear-gradient(to bottom, ${gridColor} 1px, transparent 1px)
                                        `,
                                        backgroundSize: `${gridStepDisplay}px ${gridStepDisplay}px`,
                                    }}
                                />
                            )}
                            {visibleElements.map((element) => (
                                <TemplateEditorCanvasElement
                                    key={element.id}
                                    element={element}
                                    selected={selectedIds.includes(element.id)}
                                    showHandles={selectedIds.length === 1 && selectedIds[0] === element.id}
                                    theme={theme}
                                    inlineEdit={inlineEdit}
                                    disabled={isPlacing}
                                    pointerScale={pointerScale}
                                    actions={elementActions}
                                    onSelectMove={(event) => handleElementSelectMove(element.id, event)}
                                    onResize={(handle, event) => handleElementResize(element.id, handle, event)}
                                    onContextMenu={(event) => handleElementContextMenu(element.id, event)}
                                />
                            ))}
                            {previewRect && placeMode && (
                                <TemplateEditorPlacePreview
                                    rect={previewRect}
                                    type={placeMode.type}
                                    theme={theme}
                                    opts={previewOpts}
                                />
                            )}
                            {marqueeRect && <TemplateEditorMarquee rect={marqueeRect} theme={theme} />}
                            <TemplateEditorGuideLines guides={guides} theme={theme} />
                        </div>
                    </div>
                </div>
            </div>
            {contextMenu && (
                <TemplateEditorContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    groups={contextMenu.groups}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </main>
    )
}
